"use client";

import { useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";

export interface AgentLaunchParams {
  /** Arm a skill (sent as skill_id, instruction injected server-side). */
  skillId?: string;
  skillName?: string;
  /** Raise the turn cap for the first message (skill interviews can be long). */
  skillRun?: boolean;
  /** Auto-submitted first user message. With a skill armed, this kicks off the
   *  flow immediately instead of parking in the chat box. */
  query?: string;
}

/**
 * Launch a FRESH home chat, optionally armed with a skill and/or an
 * auto-submitted first message.
 *
 * The home page reads its launch params (`?skill`, `?q`, ...) exactly once when
 * it mounts. A soft `router.push("/?...")` while ALREADY on the home page would
 * not remount it, so the new params would be ignored. We add a unique `_` param
 * which PageContentWithReset watches: when it changes it bumps the home reset
 * key, forcing a fresh mount that re-reads the new launch params (see
 * app/home-client.tsx). This makes launching work from anywhere, including the
 * home page itself.
 */
export function useAgentLaunch() {
  const router = useRouter();
  const pathname = usePathname();

  return useCallback(
    ({ skillId, skillName, skillRun, query }: AgentLaunchParams) => {
      const params = new URLSearchParams();
      if (skillId) params.set("skill", skillId);
      if (skillName) params.set("skillname", skillName);
      if (skillRun) params.set("skillrun", "1");
      if (query) params.set("q", query);
      // Force a fresh home mount (even when already on "/") so the launch
      // params actually take effect. Stripped from the URL after auto-submit.
      params.set("_", Date.now().toString(36));
      const href = `/?${params.toString()}`;
      // eslint-disable-next-line no-console
      console.log("[AGENT-LAUNCH]", { href, onHome: pathname === "/" });
      router.push(href);
    },
    [router, pathname],
  );
}
